import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { BUSINESS } from "@/data/content";

const LINKS = [
  ["Services", "#services"],
  ["Studio", "#studio"],
  ["Gallery", BUSINESS.photosLink, true],
  ["Reviews", "#reviews"],
];

export default function Navbar() {
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let last = 0;
    const onScroll = () => {
      const y = window.scrollY;
      setHidden(y > last && y > 240);
      last = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, hash) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(hash);
  };

  return (
    <>
      <motion.header
        data-testid="site-navbar"
        animate={{ y: hidden ? "-100%" : "0%" }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-0 left-0 right-0 z-40 border-b border-line bg-paper/90 backdrop-blur-md"
      >
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 lg:px-12">
          <a
            href="#top"
            onClick={(e) => go(e, "#top")}
            data-testid="nav-logo"
            className="font-serif text-lg tracking-tight"
          >
            STAR AVG
            <span className="ml-2 align-middle text-[10px] font-sans font-semibold uppercase tracking-[0.3em] text-terra">
              Enterprise
            </span>
          </a>
          <nav className="hidden items-center gap-10 md:flex">
            {LINKS.map(([label, hash, external]) => (
              <a
                key={label}
                href={hash}
                {...(external
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : { onClick: (e) => go(e, hash) })}
                data-testid={`nav-link-${label.toLowerCase()}`}
                className="text-xs font-semibold uppercase tracking-[0.2em] text-taupe transition-colors duration-300 hover:text-ink"
              >
                {label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={(e) => go(e, "#contact")}
              data-testid="nav-cta-button"
              className="group flex items-center gap-2 border border-ink px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-ink hover:text-paper"
            >
              Start a project
              <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </nav>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="md:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </motion.header>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-30 flex flex-col justify-center gap-8 bg-paper px-8 md:hidden"
          >
            {[...LINKS, ["Contact", "#contact"]].map(([label, hash, external], i) => (
              <motion.a
                key={label}
                href={hash}
                {...(external
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : { onClick: (e) => go(e, hash) })}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * i, duration: 0.5 }}
                className="font-serif text-4xl tracking-tight"
              >
                {label}
              </motion.a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
